import { useEffect, useState, useCallback } from "react";
import { Scale, Save, RefreshCw, Loader2, AlertTriangle, ShieldX, CheckCircle2, Database, SlidersHorizontal, Tag, History, FileDown, CalendarClock, BellRing, BellOff, Lock, PenLine, XCircle, ShieldCheck, Timer } from "lucide-react";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import EbrInstitucional from "@/components/riesgo/EbrInstitucional";
import { ESTADOS_MX, ACTIVIDADES_ECONOMICAS, ACTIVIDADES_VULNERABLES, ACTIVIDADES_GRUPOS } from "@/lib/catalogos_maestros";

const MOTIVO_LABEL = {
  matriz: "Grado ALTO (matriz EBR)", pep: "PEP", lista_potencial: "Coincidencia potencial",
  lista_confirmada_lpb: "LPB confirmada",
};

// Defaults de escalas para versiones legacy que aún no las tengan.
const DEFAULT_ESCALAS_TP = {
  fisica_nacional: "bajo",
  fisica_extranjera: "medio",
  moral_nacional: "medio",
  moral_extranjera: "alto",
  fideicomiso: "alto",
};
const DEFAULT_ESCALAS_MF = { corte_medio: 50000, corte_alto: 250000, unidad: "MXN/mes" };
const DEFAULT_ESCALAS_UE = { corte_medio_pct: 15, corte_alto_pct: 40 };
const DEFAULT_ESCALAS_MP = { efectivo: "alto", transferencia: "bajo", cheque: "medio", tarjeta: "bajo", domiciliacion: "bajo" };
const DEFAULT_ESCALAS_CANAL = { presencial: "bajo", no_presencial: "medio", digital: "medio", corresponsal: "medio", app: "alto" };
const TP_ORDER = [
  { key: "fisica_nacional", label: "Personas físicas nacionales" },
  { key: "fisica_extranjera", label: "Personas físicas extranjeras" },
  { key: "moral_nacional", label: "Personas morales nacionales" },
  { key: "moral_extranjera", label: "Personas morales extranjeras" },
  { key: "fideicomiso", label: "Fideicomisos" },
];
const MP_ORDER = [
  { key: "efectivo", label: "Efectivo" },
  { key: "transferencia", label: "Transferencia" },
  { key: "cheque", label: "Cheque" },
  { key: "tarjeta", label: "Tarjeta" },
  { key: "domiciliacion", label: "Domiciliación" },
];
const CANAL_ORDER = [
  { key: "presencial", label: "Presencial" },
  { key: "no_presencial", label: "No presencial" },
  { key: "digital", label: "Digital" },
  { key: "corresponsal", label: "Corresponsal" },
  { key: "app", label: "App" },
];
const NIVEL_COLOR = { bajo: "bg-emerald-100 text-emerald-700",
                      medio: "bg-amber-100 text-amber-700",
                      alto: "bg-red-100 text-red-700" };

// Editor de catálogo con selector CERRADO desde una lista maestra (opciones controladas).
// Uso: zonas de alto riesgo (32 estados) y actividades de alto riesgo (catálogo curado).
function CatalogoSelectChips({ label, testid, items, options, placeholder, onAdd, onRemove, disabled, itemLabel }) {
  const [val, setVal] = useState("");
  const disponibles = (options || []).filter((o) => !(items || []).includes(o.value || o));
  return (
    <div>
      <p className="mb-1 text-xs text-slate-500">{label}</p>
      <div className="flex flex-wrap gap-1.5" data-testid={`cfg-${testid}`}>
        {(items || []).length === 0 && <span className="text-xs text-slate-400">Sin elementos configurados.</span>}
        {(items || []).map((z) => (
          <Badge key={z} variant="outline" className="max-w-full whitespace-normal break-words text-left leading-tight" data-testid={`cfg-${testid}-item-${z}`}>{itemLabel ? itemLabel(z) : z}
            {!disabled && <button onClick={() => onRemove(z)} className="ml-1 text-slate-400 hover:text-red-600">×</button>}
          </Badge>
        ))}
      </div>
      {!disabled && (
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
          <select className="h-8 w-full min-w-0 flex-1 rounded border border-slate-300 px-2 text-sm sm:w-auto" data-testid={`cfg-${testid}-select`}
            value={val} onChange={(e) => setVal(e.target.value)}>
            <option value="">{placeholder}</option>
            {disponibles.map((o) => (
              <option key={o.value || o} value={o.value || o}>{o.label || o.value || o}</option>
            ))}
          </select>
          <Button data-testid={`cfg-${testid}-add`} size="sm" variant="outline"
            className="w-full shrink-0 whitespace-nowrap sm:w-auto"
            onClick={() => { if (val) { onAdd(val); setVal(""); } }}>Agregar</Button>
        </div>
      )}
    </div>
  );
}

// Editor de catálogo de chips reutilizable (zonas / productos / actividades).
function CatalogoChips({ label, testid, items, placeholder, onAdd, onRemove, disabled }) {
  const [val, setVal] = useState("");
  return (
    <div>
      <p className="mb-1 text-xs text-slate-500">{label}</p>
      <div className="flex flex-wrap gap-1.5" data-testid={`cfg-${testid}`}>
        {(items || []).length === 0 && <span className="text-xs text-slate-400">Sin elementos configurados.</span>}
        {(items || []).map((z) => (
          <Badge key={z} variant="outline" data-testid={`cfg-${testid}-item-${z}`}>{z}
            {!disabled && <button onClick={() => onRemove(z)} className="ml-1 text-slate-400 hover:text-red-600">×</button>}
          </Badge>
        ))}
      </div>
      {!disabled && (
        <div className="mt-2 flex gap-2">
          <input className="h-8 flex-1 rounded border border-slate-300 px-2 text-sm" data-testid={`cfg-${testid}-input`}
            placeholder={placeholder} value={val} onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { onAdd(val.trim()); setVal(""); } }} />
          <Button data-testid={`cfg-${testid}-add`} size="sm" variant="outline"
            onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(""); } }}>Agregar</Button>
        </div>
      )}
    </div>
  );
}

export default function RiesgoEBR() {
  const { user } = useAuth();
  const role = user?.role;
  const puedeEditar = ["super_admin", "admin_entidad", "oficial_cumplimiento"].includes(role);
  const puedeCatalogos = ["admin_entidad", "oficial_cumplimiento"].includes(role);
  const esSuperAdmin = role === "super_admin";

  const [stats, setStats] = useState(null);
  const [met, setMet] = useState(null);
  const [bandeja, setBandeja] = useState(null);
  const [cfg, setCfg] = useState({ zonas_alto_riesgo: [], productos_alto_riesgo: [], actividades_alto_riesgo: [] });
  // Evidencia visible de guardado de catálogos (feb-2026). Cargada desde el GET.
  const [cfgMeta, setCfgMeta] = useState({ por: null, rol: null, fecha: null, diff: [] });
  const [productosEntidad, setProductosEntidad] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [revision, setRevision] = useState(null);
  const [enrOpen, setEnrOpen] = useState(false);
  const [enrMotivo, setEnrMotivo] = useState("");
  const [enrBusy, setEnrBusy] = useState(false);
  // Cambio 1: dialog explícito de confirmación de sellado 12 meses.
  const [confirmSellado, setConfirmSellado] = useState(false);
  const [confirmModo, setConfirmModo] = useState(null); // "linea_base" | "revision"
  const [saving, setSaving] = useState(false);
  const [savingCat, setSavingCat] = useState(false);
  const [recal, setRecal] = useState(false);

  const load = useCallback(async () => {
    const [s, m, b] = await Promise.all([
      api.get("/riesgo/dashboard").then((r) => r.data),
      api.get("/riesgo/metodologia").then((r) => r.data),
      api.get("/riesgo/bandeja").then((r) => r.data),
    ]);
    setStats(s);
    setMet({ ...m, umbrales: m.umbrales || { medio: 40, alto: 70, eleva_subfactor: 67 } });
    setBandeja(b);
    setRevision(s.revision_metodologia || null);
    if (puedeEditar) {
      const hist = await api.get("/riesgo/metodologia/historial").then((r) => r.data.versiones).catch(() => []);
      setHistorial(hist || []);
    }
    if (puedeCatalogos) {
      const c = await api.get("/pld/gafi/factores-config").then((r) => r.data)
        .catch(() => ({ zonas_alto_riesgo: [], productos_alto_riesgo: [], actividades_alto_riesgo: [] }));
      setCfg({ zonas_alto_riesgo: c.zonas_alto_riesgo || [], productos_alto_riesgo: c.productos_alto_riesgo || [], actividades_alto_riesgo: c.actividades_alto_riesgo || [] });
      setCfgMeta({
        por: c.catalogos_guardado_por || null,
        rol: c.catalogos_guardado_rol || null,
        fecha: c.catalogos_guardado_fecha || null,
        diff: c.catalogos_ultimo_diff || [],
      });
      // Cambio 4: productos de crédito de la entidad para poblar el select de "productos de alto riesgo".
      // feb-2026: filtra a ACTIVOS únicamente para que el OC no marque como alto riesgo un producto retirado.
      const productos = await api.get("/productos_credito").then((r) => r.data || []).catch(() => []);
      setProductosEntidad(productos.filter((p) => p.activo !== false));
    }
  }, [puedeCatalogos, puedeEditar]);
  useEffect(() => { load(); }, [load]);

  const marcarEnr = async (pendiente) => {
    if (pendiente && enrMotivo.trim().length < 5) { toast.error("Indica el motivo o referencia de la nueva ENR (mínimo 5 caracteres)."); return; }
    setEnrBusy(true);
    try {
      await api.post("/riesgo/metodologia/marcar-revision-enr", { pendiente, motivo: enrMotivo });
      toast.success(pendiente ? "Revisión marcada como pendiente por nueva ENR." : "Marca de revisión por ENR retirada.");
      setEnrOpen(false); setEnrMotivo("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEnrBusy(false); }
  };

  const descargarEvidencia = async (ver) => {
    try {
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}/riesgo/metodologia/${ver.id}/evidencia`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("No se pudo generar el PDF de evidencia.");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `Metodologia_EBR_v${ver.version}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
  };

  // Estado del candado y firmas (Fase 1 sellado EBR).
  const estadoEdicion = met?.estado_edicion || "primera_config";
  const bloqueada = estadoEdicion === "bloqueada";
  const enPrimeraConfig = estadoEdicion === "primera_config";
  const enRevision = ["revision_anual", "revision_enr"].includes(estadoEdicion);
  const borrador = met?.borrador || null;
  const firmasActuales = borrador?.firmas || {};
  const yaFirmoRol = !!firmasActuales[role];
  const rolPendiente = firmasActuales.admin_entidad && !firmasActuales.oficial_cumplimiento
    ? "oficial_cumplimiento"
    : firmasActuales.oficial_cumplimiento && !firmasActuales.admin_entidad
      ? "admin_entidad" : null;
  // Feb-2026 · Fix P1 iter-104 (Tarea 1). Si soy el firmante pendiente de un borrador que ya
  // fue propuesto y firmado por la contraparte, DEBO ver los valores del BORRADOR (no los
  // vigentes) y NO debo poder re-proponer valores distintos: solo firmar. Si intentase re-
  // proponer, sobrescribiría silenciosamente la propuesta original — problema regulatorio serio.
  const esFirmantePendiente = !!(rolPendiente && role === rolPendiente && borrador);
  // Habilitación de edición de inputs metodológicos (ponderaciones/umbrales/escalas).
  const puedeEditarMet = puedeEditar && !bloqueada
    && !(enPrimeraConfig && role !== "admin_entidad")
    && !esFirmantePendiente;  // firmante pendiente NO edita: solo firma lo propuesto
  // Cuando soy firmante pendiente, los valores mostrados en los inputs son los del BORRADOR.
  // Cuando NO soy firmante pendiente, los valores son los VIGENTES (edición normal / lectura).
  const vistaFactores = esFirmantePendiente && borrador?.factores ? borrador.factores : met?.factores;
  const vistaUmbrales = esFirmantePendiente && borrador?.umbrales ? borrador.umbrales : (met?.umbrales || { medio: 40, alto: 70, eleva_subfactor: 67 });
  const escalasBorrador = esFirmantePendiente ? (borrador?.escalas || {}) : null;
  const escalasTp = (escalasBorrador?.tipo_persona) || met?.escalas?.tipo_persona || DEFAULT_ESCALAS_TP;
  const escalasMf = (escalasBorrador?.monto_frecuencia) || met?.escalas?.monto_frecuencia || DEFAULT_ESCALAS_MF;
  const escalasUe = (escalasBorrador?.uso_efectivo) || met?.escalas?.uso_efectivo || DEFAULT_ESCALAS_UE;
  const escalasMp = (escalasBorrador?.medio_pago) || met?.escalas?.medio_pago || DEFAULT_ESCALAS_MP;
  const escalasCanal = (escalasBorrador?.canal) || met?.escalas?.canal || DEFAULT_ESCALAS_CANAL;

  const suma = vistaFactores ? vistaFactores.reduce((a, f) => a + Number(f.ponderacion || 0), 0) : 0;
  const u = vistaUmbrales;
  const umbralesOk = Number(u.medio) >= 0 && Number(u.medio) < Number(u.alto) && Number(u.alto) <= 100
    && Number(u.eleva_subfactor) >= 0 && Number(u.eleva_subfactor) <= 100;

  // Cambio 1: contador de días hasta el desbloqueo anual.
  const diasParaDesbloqueo = (() => {
    if (!met?.desbloqueable_desde) return null;
    try {
      const t = new Date(met.desbloqueable_desde + "T00:00:00").getTime();
      return Math.ceil((t - Date.now()) / 86400000);
    } catch { return null; }
  })();

  // Cambio 1: puede iniciar revisión? (habilitación del botón "Editar metodología")
  const puedeIniciarRevision = (
    enPrimeraConfig ||    // línea base todavía pendiente → siempre editable
    enRevision ||         // ya está en ventana (revision_anual / revision_enr)
    (met?.revision_pendiente_enr === true)
  );

  const setPond = (clave, val) => setMet((p) => ({
    ...p, factores: p.factores.map((f) => f.clave === clave ? { ...f, ponderacion: Number(val) } : f),
  }));
  const setUmbral = (k, val) => setMet((p) => ({ ...p, umbrales: { ...p.umbrales, [k]: Number(val) } }));
  const setEscalaTp = (k, nivel) => setMet((p) => ({
    ...p, escalas: { ...(p.escalas || {}), tipo_persona: { ...(p.escalas?.tipo_persona || DEFAULT_ESCALAS_TP), [k]: nivel } },
  }));
  const setEscalaMf = (k, val) => setMet((p) => ({
    ...p, escalas: { ...(p.escalas || {}), monto_frecuencia: { ...(p.escalas?.monto_frecuencia || DEFAULT_ESCALAS_MF), [k]: Number(val) } },
  }));
  const setEscalaUe = (k, val) => setMet((p) => ({
    ...p, escalas: { ...(p.escalas || {}), uso_efectivo: { ...(p.escalas?.uso_efectivo || DEFAULT_ESCALAS_UE), [k]: Number(val) } },
  }));
  const setEscalaMp = (k, nivel) => setMet((p) => ({
    ...p, escalas: { ...(p.escalas || {}), medio_pago: { ...(p.escalas?.medio_pago || DEFAULT_ESCALAS_MP), [k]: nivel } },
  }));
  const setEscalaCanal = (k, nivel) => setMet((p) => ({
    ...p, escalas: { ...(p.escalas || {}), canal: { ...(p.escalas?.canal || DEFAULT_ESCALAS_CANAL), [k]: nivel } },
  }));

  const enviarMetodologia = async (etiqueta) => {
    if (suma !== 100) { toast.error(`Las ponderaciones deben sumar 100% (actual: ${suma}%)`); return null; }
    if (!umbralesOk) { toast.error("Umbrales incoherentes: debe cumplirse 0 ≤ Medio < Alto ≤ 100."); return null; }
    setSaving(true);
    try {
      const { data } = await api.put("/riesgo/metodologia", {
        factores: met.factores, umbrales: met.umbrales,
        escalas: {
          tipo_persona: escalasTp,
          monto_frecuencia: escalasMf,
          uso_efectivo: escalasUe,
          medio_pago: escalasMp,
          canal: escalasCanal,
        },
      });
      if (data?.sellada_por_doble_firma) {
        toast.success("Metodología EBR sellada por doble firma. Bloqueada por 12 meses.");
      } else if (data?.requiere_firma_pendiente) {
        toast.success(`Propuesta guardada. Falta la firma de ${data.rol_pendiente} para sellar.`);
      } else {
        toast.success(etiqueta || "Metodología EBR actualizada.");
      }
      await load();
      return data;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "metodologia_bloqueada") {
        toast.error(detail.message);
      } else {
        toast.error(apiError(e));
      }
      return null;
    } finally { setSaving(false); }
  };

  const guardar = () => enviarMetodologia("Cambios guardados en el borrador de revisión.");

  // "Definir línea base y sellar" — solo visible en primera_config, exclusivo admin_entidad.
  const sellarLineaBase = () => enviarMetodologia("Metodología EBR sellada por primera vez.");

  const firmar = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/riesgo/metodologia/firmar");
      if (data?.sellada_por_doble_firma) {
        toast.success("Metodología EBR sellada por doble firma. Bloqueada por 12 meses.");
      } else {
        toast.success(`Tu firma quedó registrada. Falta la firma de ${data.rol_pendiente}.`);
      }
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const cancelarBorrador = async () => {
    if (!confirm("¿Cancelar el borrador de revisión? Se perderán las propuestas de esta ventana.")) return;
    setSaving(true);
    try {
      await api.post("/riesgo/metodologia/borrador/cancelar");
      toast.success("Borrador de revisión cancelado.");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const guardarCatalogos = async () => {
    setSavingCat(true);
    try {
      const { data } = await api.put("/pld/gafi/factores-config", cfg);
      const nP = (data.productos_alto_riesgo || []).length;
      const nZ = (data.zonas_alto_riesgo || []).length;
      const nA = (data.actividades_alto_riesgo || []).length;
      toast.success(`Catálogos guardados: ${nP} producto${nP !== 1 ? "s" : ""}, ${nZ} zona${nZ !== 1 ? "s" : ""}, ${nA} actividad${nA !== 1 ? "es" : ""}.`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingCat(false); }
  };

  const recalcular = async () => {
    setRecal(true);
    try {
      const { data } = await api.post("/riesgo/recalcular");
      toast.success(`Grados recalculados (masivo): ${data.recalculados} clientes`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRecal(false); }
  };

  const addCat = (key, v) => setCfg((p) => p[key].includes(v) ? p : ({ ...p, [key]: [...p[key], v] }));
  const rmCat = (key, v) => setCfg((p) => ({ ...p, [key]: p[key].filter((x) => x !== v) }));

  return (
    <div data-testid="riesgo-ebr">
      {/* EBR institucional con datos reales (43ª-IX Bis) */}
      <EbrInstitucional />

      <div className="mb-4 flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        <Scale className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span><b>Metodología EBR de la entidad</b> (Enfoque Basado en Riesgos, 5 factores). El grado de cada cliente se calcula <b>automáticamente</b> al alta cruzando su expediente contra estos catálogos y ponderaciones. Cambios aquí aplican solo a clientes nuevos y a recálculos puntuales del OC. Revisión cada 12 meses; conservación 5 años.</span>
      </div>

      {/* Aviso de revisión de la metodología EBR (disp. 17a-4): por tiempo (12m) y por nueva ENR */}
      {revision && (
        <div className={`mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${
          revision.semaforo === "rojo" ? "border-red-200 bg-red-50"
          : revision.semaforo === "ambar" ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}
          data-testid="ebr-revision-banner">
          <div className="flex items-start gap-2">
            <CalendarClock className={`mt-0.5 h-5 w-5 shrink-0 ${revision.semaforo === "rojo" ? "text-red-600" : revision.semaforo === "ambar" ? "text-amber-600" : "text-emerald-600"}`} />
            <div>
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Badge data-testid="ebr-revision-badge" className={
                  revision.semaforo === "rojo" ? "bg-red-100 text-red-700"
                  : revision.semaforo === "ambar" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>
                  {revision.etiqueta}
                </Badge>
                <span className="text-slate-600">Metodología v{revision.version}</span>
              </p>
              <p className="mt-0.5 text-xs text-slate-500">
                {revision.estado === "enr_pendiente"
                  ? <>Marcada por nueva ENR{revision.enr_motivo ? `: ${revision.enr_motivo}` : ""}{revision.enr_marcada_por ? ` — por ${revision.enr_marcada_por}` : ""}</>
                  : <>Aprobada {revision.aprobado_fecha} · límite de revisión {revision.fecha_limite} ({revision.dias_restantes} día(s)). {revision.fundamento}</>}
              </p>
              {revision.tarea_remediacion && revision.tarea_remediacion.estatus === "abierta" && (
                <p className="mt-1 text-xs font-medium text-slate-700" data-testid="ebr-tarea-remediacion">
                  📌 Tarea de remediación: responsable <b>{revision.tarea_remediacion.responsable_nombre}</b> (Oficial de Cumplimiento) · fecha objetivo <b>{revision.tarea_remediacion.fecha_objetivo}</b>. Se cerrará automáticamente al publicar una nueva versión.
                </p>
              )}
            </div>
          </div>
          {puedeCatalogos && (
            revision.revision_pendiente_enr ? (
              <Button data-testid="ebr-enr-desmarcar-btn" size="sm" variant="outline" disabled={enrBusy} onClick={() => marcarEnr(false)}>
                {enrBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BellOff className="mr-1.5 h-4 w-4" />} Quitar marca ENR
              </Button>
            ) : !enrOpen ? (
              <Button data-testid="ebr-enr-marcar-btn" size="sm" variant="outline" onClick={() => setEnrOpen(true)}>
                <BellRing className="mr-1.5 h-4 w-4" /> Marcar revisión por nueva ENR
              </Button>
            ) : (
              <div className="flex w-full items-center gap-2 sm:w-auto" data-testid="ebr-enr-form">
                <Input data-testid="ebr-enr-motivo" value={enrMotivo} onChange={(e) => setEnrMotivo(e.target.value)}
                  placeholder="Motivo / referencia de la ENR" className="h-8 w-56" />
                <Button data-testid="ebr-enr-confirmar-btn" size="sm" disabled={enrBusy} onClick={() => marcarEnr(true)} className="bg-primary hover:bg-sidebar-hover">
                  {enrBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Marcar"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEnrOpen(false); setEnrMotivo(""); }}>Cancelar</Button>
              </div>
            )
          )}
        </div>
      )}

      {/* Cambio 1: recuadro VISIBLE con candado + contador + botón "Editar metodología" */}
      {met && bloqueada && (
        <div data-testid="ebr-candado-bloqueada"
          className="mb-4 rounded-lg border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-emerald-100 p-2">
                <Lock className="h-5 w-5 text-emerald-700" />
              </div>
              <div>
                <p className="font-semibold text-emerald-900">Metodología EBR sellada y vigente</p>
                <p className="mt-0.5 text-sm text-emerald-800">
                  <span data-testid="ebr-sellado-info">
                    v{met.version} · sellada por <b>{met.sellado_por}</b>
                    {met.sellado_fecha ? ` el ${met.sellado_fecha}` : ""}
                  </span>
                </p>
                <p className="mt-1.5 text-sm text-slate-700">
                  Próxima edición disponible: <b data-testid="ebr-desbloqueo-fecha">{met.desbloqueable_desde || "por definir"}</b>
                  {diasParaDesbloqueo !== null && diasParaDesbloqueo > 0 && (
                    <> · <span data-testid="ebr-dias-restantes" className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"><Timer className="h-3 w-3" /> Faltan {diasParaDesbloqueo} días</span></>
                  )}
                </p>
                <p className="mt-1.5 text-xs text-slate-500">
                  Podrás editar antes si el Oficial de Cumplimiento marca revisión por nueva ENR.
                  Los catálogos de alto riesgo permanecen editables como controles operativos.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Button data-testid="ebr-editar-metodologia-btn" size="sm" variant="outline" disabled={!puedeIniciarRevision}
                title={!puedeIniciarRevision ? `Bloqueada hasta ${met.desbloqueable_desde || 'aniversario anual'}. Puedes marcar revisión por ENR para abrir la ventana antes.` : ""}
                onClick={() => toast.info("Marca revisión por nueva ENR para abrir la ventana de edición.")}>
                <Lock className="mr-1.5 h-4 w-4" /> Editar metodología
              </Button>
              <span className="text-[10px] text-slate-500 max-w-[220px] text-right">Deshabilitado hasta ventana anual o revisión por ENR</span>
            </div>
          </div>
        </div>
      )}
      {met && enPrimeraConfig && (
        <div data-testid="ebr-candado-primera-config"
          className="mb-4 rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-amber-100 p-2"><AlertTriangle className="h-5 w-5 text-amber-700" /></div>
            <div>
              <p className="font-semibold text-amber-900">Línea base pendiente de aprobación</p>
              <p className="mt-0.5 text-sm text-amber-800">
                Configure las ponderaciones, umbrales, escalas y catálogos, luego apruebe como
                <b> admin_entidad</b> para <b>sellar la línea base</b>. Al sellar, la metodología
                quedará bloqueada por <b>12 meses</b> (revisión anual) y solo podrá modificarse al
                marcar una revisión por nueva ENR.
              </p>
            </div>
          </div>
        </div>
      )}
      {met && enRevision && (
        <div data-testid="ebr-candado-revision"
          className="mb-4 rounded-lg border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-amber-100 p-2"><PenLine className="h-5 w-5 text-amber-700" /></div>
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                Ventana de revisión abierta · {estadoEdicion === "revision_enr" ? "Detonante: nueva ENR" : "Detonante: aniversario anual"}
              </p>
              <p className="mt-0.5 text-sm text-amber-800">
                Puedes editar la metodología. La nueva versión se sella <b>solo cuando ambas firmas
                estén presentes</b>: <b>admin_entidad</b> + <b>Oficial de Cumplimiento</b>.
              </p>
              {borrador && (
                <div data-testid="ebr-borrador-firmas" className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-slate-700 text-xs">Firmas del borrador:</span>
                  <Badge className={firmasActuales.admin_entidad ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>
                    {firmasActuales.admin_entidad ? `✓ admin_entidad · ${firmasActuales.admin_entidad.nombre}` : "○ admin_entidad (pendiente)"}
                  </Badge>
                  <Badge className={firmasActuales.oficial_cumplimiento ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>
                    {firmasActuales.oficial_cumplimiento ? `✓ oficial_cumplimiento · ${firmasActuales.oficial_cumplimiento.nombre}` : "○ oficial_cumplimiento (pendiente)"}
                  </Badge>
                  {rolPendiente && (role === rolPendiente) && (
                    <Button data-testid="ebr-firmar-btn" size="sm" onClick={firmar} disabled={saving}
                      className="bg-emerald-600 text-white hover:bg-emerald-700">
                      {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PenLine className="mr-1.5 h-4 w-4" />} Firmar como {role}
                    </Button>
                  )}
                  <Button data-testid="ebr-cancelar-borrador-btn" size="sm" variant="outline" onClick={cancelarBorrador} disabled={saving}>
                    <XCircle className="mr-1.5 h-4 w-4" /> Cancelar borrador
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {esFirmantePendiente && borrador && (
        <div data-testid="ebr-banner-firmar-borrador"
          className="mb-4 rounded-lg border-2 border-blue-300 bg-blue-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-blue-100 p-2"><PenLine className="h-5 w-5 text-blue-700" /></div>
            <div className="flex-1">
              <p className="font-semibold text-blue-900">Tu firma sella este borrador de metodología EBR</p>
              <p className="mt-0.5 text-sm text-blue-800">
                Estos son los valores exactos que se sellarán al firmar. Se muestran los propuestos
                por <b>{firmasActuales.admin_entidad?.nombre || firmasActuales.oficial_cumplimiento?.nombre || "la contraparte"}</b>
                — no puedes modificarlos aquí. Si necesitas cambiarlos, cancela el borrador y propón uno nuevo.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded bg-white/70 px-2 py-1.5" data-testid="ebr-borrador-preview-umbrales">
                  <span className="font-medium text-slate-600">Umbrales semáforo:</span>
                  <span className="ml-1 text-slate-900">Medio ≥ {u.medio} · Alto ≥ {u.alto} · Eleva ≥ {u.eleva_subfactor}</span>
                </div>
                <div className="rounded bg-white/70 px-2 py-1.5" data-testid="ebr-borrador-preview-mf">
                  <span className="font-medium text-slate-600">Monto/frecuencia (MXN/mes):</span>
                  <span className="ml-1 text-slate-900">Medio ≥ {escalasMf.corte_medio?.toLocaleString?.("es-MX") || escalasMf.corte_medio} · Alto ≥ {escalasMf.corte_alto?.toLocaleString?.("es-MX") || escalasMf.corte_alto}</span>
                </div>
                <div className="rounded bg-white/70 px-2 py-1.5" data-testid="ebr-borrador-preview-ue">
                  <span className="font-medium text-slate-600">Uso efectivo (%):</span>
                  <span className="ml-1 text-slate-900">Medio ≥ {escalasUe.corte_medio_pct}% · Alto ≥ {escalasUe.corte_alto_pct}%</span>
                </div>
                <div className="rounded bg-white/70 px-2 py-1.5" data-testid="ebr-borrador-preview-mp">
                  <span className="font-medium text-slate-600">Efectivo:</span>
                  <span className="ml-1 text-slate-900">{(escalasMp.efectivo || "bajo").toUpperCase()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cambio 1: Dialog de confirmación explícito antes de sellar (12 meses de bloqueo) */}
      <Dialog open={confirmSellado} onOpenChange={setConfirmSellado}>
        <DialogContent data-testid="ebr-dialog-confirmar-sellado" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" />
              {confirmModo === "linea_base" ? "Confirmar sellado de línea base" : "Confirmar propuesta de revisión"}
            </DialogTitle>
            <DialogDescription>
              {confirmModo === "linea_base"
                ? <>Al sellar, esta metodología quedará <b>FIJA durante 12 meses</b>. Solo podrá modificarse a partir de <b>{met?.desbloqueable_desde || "el aniversario anual"}</b> o cuando el Oficial de Cumplimiento marque una revisión por nueva ENR. ¿Confirmas el sellado?</>
                : <>Registrarás tu firma en el borrador de revisión. Se requiere <b>doble firma (admin_entidad + Oficial de Cumplimiento)</b> para sellar la nueva versión. Una vez sellada, la metodología queda bloqueada por otros <b>12 meses</b>. ¿Confirmas?</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSellado(false)}>Cancelar</Button>
            <Button data-testid="ebr-confirmar-sellado-btn" className="bg-emerald-600 text-white hover:bg-emerald-700"
              onClick={async () => {
                setConfirmSellado(false);
                if (confirmModo === "linea_base") await sellarLineaBase();
                else await guardar();
              }}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
              {confirmModo === "linea_base" ? "Sí, sellar por 12 meses" : "Sí, registrar firma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {stats && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Verde (bajo)" value={stats.verde} icon="CheckCircle2" accent="green" />
          <StatCard label="Amarillo (medio)" value={stats.amarillo} icon="AlertTriangle" accent="primary" />
          <StatCard label="Rojo (alto)" value={stats.rojo} icon="ShieldX" accent="red" />
          <StatCard label="Pend. autorización" value={stats.pendientes_override} icon="Clock" accent="red" />
          <StatCard label="Bloqueados (LPB)" value={stats.bloqueados} icon="Ban" accent="red" />
          <StatCard label="Sin clasificar" value={stats.sin_clasificar} icon="HelpCircle" accent="primary" />
        </div>
      )}

      {/* Aviso duplicado eliminado (lo reemplaza el banner de estado_edicion arriba). */}

      {met && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-900">
              <SlidersHorizontal className="h-4 w-4 text-primary" /> Metodología EBR — ponderaciones, umbrales y escalas (v{met.version})
              {bloqueada && <Lock data-testid="ebr-icono-candado" className="h-4 w-4 text-emerald-700" />}
            </h3>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${suma === 100 ? "text-emerald-600" : "text-red-600"}`} data-testid="ebr-suma">Suma: {suma}%</span>
              {esSuperAdmin && (
                <Button data-testid="ebr-recalcular-btn" size="sm" variant="outline" onClick={recalcular} disabled={recal}>
                  {recal ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Recálculo masivo
                </Button>
              )}
              {enPrimeraConfig && role === "admin_entidad" && (
                <Button data-testid="ebr-sellar-linea-base-btn" size="sm"
                  onClick={() => { setConfirmModo("linea_base"); setConfirmSellado(true); }}
                  disabled={saving}
                  className="bg-emerald-600 text-white hover:bg-emerald-700">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} Definir línea base y sellar
                </Button>
              )}
              {enRevision && !esFirmantePendiente && (
                <Button data-testid="ebr-guardar-btn" size="sm"
                  onClick={() => { setConfirmModo("revision"); setConfirmSellado(true); }}
                  disabled={saving || !puedeEditarMet} className="bg-primary hover:bg-sidebar-hover">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} {yaFirmoRol ? "Actualizar borrador" : "Proponer y firmar"}
                </Button>
              )}
              {esFirmantePendiente && (
                <Button data-testid="ebr-firmar-btn-topbar" size="sm" onClick={firmar} disabled={saving}
                  className="bg-emerald-600 text-white hover:bg-emerald-700">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PenLine className="mr-1.5 h-4 w-4" />} Firmar borrador propuesto
                </Button>
              )}
            </div>
          </div>

          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Ponderación por factor</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {vistaFactores.map((f) => (
              <div key={f.clave} className="rounded-md border border-slate-200 p-3">
                <p className="text-sm font-medium text-slate-800">{f.nombre}</p>
                <p className="mb-2 text-[11px] text-slate-400">{f.subvariables.map((s) => s.nombre).join(" · ")}</p>
                <div className="flex items-center gap-1.5">
                  <Input data-testid={`ebr-pond-${f.clave}`} type="number" min="0" max="100" value={f.ponderacion}
                    onChange={(e) => setPond(f.clave, e.target.value)} className="h-8 w-20" disabled={!puedeEditarMet} />
                  <span className="text-sm text-slate-500">%</span>
                </div>
              </div>
            ))}
          </div>

          {/* Umbrales del semáforo (fuente única, compartida con la EBR institucional) */}
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="ebr-umbrales">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Umbrales del semáforo (puntaje 0–100)</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <p className="mb-1 text-xs text-slate-500">🟡 Amarillo (Medio) a partir de</p>
                <div className="flex items-center gap-1.5"><Input data-testid="ebr-umbral-medio" type="number" min="0" max="100" value={u.medio} onChange={(e) => setUmbral("medio", e.target.value)} className="h-8 w-24" disabled={!puedeEditarMet} /><span className="text-sm text-slate-500">pts</span></div>
              </div>
              <div>
                <p className="mb-1 text-xs text-slate-500">🔴 Rojo (Alto) a partir de</p>
                <div className="flex items-center gap-1.5"><Input data-testid="ebr-umbral-alto" type="number" min="0" max="100" value={u.alto} onChange={(e) => setUmbral("alto", e.target.value)} className="h-8 w-24" disabled={!puedeEditarMet} /><span className="text-sm text-slate-500">pts</span></div>
              </div>
              <div>
                <p className="mb-1 text-xs text-slate-500">Factor que eleva a partir de</p>
                <div className="flex items-center gap-1.5"><Input data-testid="ebr-umbral-eleva" type="number" min="0" max="100" value={u.eleva_subfactor} onChange={(e) => setUmbral("eleva_subfactor", e.target.value)} className="h-8 w-24" disabled={!puedeEditarMet} /><span className="text-sm text-slate-500">pts</span></div>
              </div>
            </div>
            <p className={`mt-2 text-xs ${umbralesOk ? "text-slate-400" : "text-red-600 font-medium"}`} data-testid="ebr-umbrales-hint">
              {umbralesOk ? "Debe cumplirse 0 ≤ Medio < Alto ≤ 100. Estos cortes los usan tanto el score por-cliente como la EBR institucional." : "Umbrales incoherentes: 0 ≤ Medio < Alto ≤ 100."}
            </p>
          </div>

          {/* Escala de Tipo de Persona (Fase 1 configurable) */}
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="ebr-escala-tipo-persona">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Escala de valoración — Tipo de persona</p>
            <p className="mb-3 text-xs text-slate-500">Define el nivel base (bajo/medio/alto) que aporta el tipo de persona del cliente al factor Cliente/usuario. Se aplica al calcular el grado de riesgo de cada nuevo cliente o al recalcular puntualmente.</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {TP_ORDER.map(({ key, label }) => (
                <div key={key} className="rounded-md border border-slate-200 bg-white px-2.5 py-2" data-testid={`ebr-escala-tp-${key}`}>
                  <p className="text-xs text-slate-500">{label}</p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <select data-testid={`ebr-escala-tp-${key}-select`} className="h-8 flex-1 rounded border border-slate-300 px-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                      value={escalasTp[key] || "bajo"} disabled={!puedeEditarMet}
                      onChange={(e) => setEscalaTp(key, e.target.value)}>
                      <option value="bajo">Bajo</option>
                      <option value="medio">Medio</option>
                      <option value="alto">Alto</option>
                    </select>
                    <Badge className={NIVEL_COLOR[escalasTp[key] || "bajo"]}>{(escalasTp[key] || "bajo").toUpperCase()}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Escala Monto/frecuencia (Fase 2) — 2 cortes numéricos MXN/mes */}
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="ebr-escala-monto-frecuencia">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Escala de valoración — Monto y frecuencia (MXN/mes)</p>
            <p className="mb-3 text-xs text-slate-500">Umbrales del monto mensual estimado en el expediente del cliente (<b>perfil_monto_mensual_mxn</b>). Si el cliente no tiene este dato capturado, se considera &quot;bajo&quot; por default.</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
                <p className="text-xs text-slate-500">🟡 Medio a partir de (MXN/mes)</p>
                <div className="mt-1 flex items-center gap-1.5">
                  <Input data-testid="ebr-escala-mf-corte-medio" type="number" min="0" value={escalasMf.corte_medio}
                    onChange={(e) => setEscalaMf("corte_medio", e.target.value)} className="h-8 flex-1" disabled={!puedeEditarMet} />
                </div>
              </div>
              <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
                <p className="text-xs text-slate-500">🔴 Alto a partir de (MXN/mes)</p>
                <div className="mt-1 flex items-center gap-1.5">
                  <Input data-testid="ebr-escala-mf-corte-alto" type="number" min="0" value={escalasMf.corte_alto}
                    onChange={(e) => setEscalaMf("corte_alto", e.target.value)} className="h-8 flex-1" disabled={!puedeEditarMet} />
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-400">Debe cumplirse Medio &lt; Alto. Se aplica al calcular el grado de riesgo automáticamente.</p>
          </div>

          {/* Escala Uso de efectivo (Fase 2) — 2 cortes en % */}
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="ebr-escala-uso-efectivo">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Escala de valoración — Uso de efectivo (%)</p>
            <p className="mb-3 text-xs text-slate-500">Umbrales del % del volumen mensual en efectivo (<b>perfil_uso_efectivo_pct</b>). Rango 0-100.</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
                <p className="text-xs text-slate-500">🟡 Medio a partir de (%)</p>
                <Input data-testid="ebr-escala-ue-corte-medio" type="number" min="0" max="100" value={escalasUe.corte_medio_pct}
                  onChange={(e) => setEscalaUe("corte_medio_pct", e.target.value)} className="h-8" disabled={!puedeEditarMet} />
              </div>
              <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2">
                <p className="text-xs text-slate-500">🔴 Alto a partir de (%)</p>
                <Input data-testid="ebr-escala-ue-corte-alto" type="number" min="0" max="100" value={escalasUe.corte_alto_pct}
                  onChange={(e) => setEscalaUe("corte_alto_pct", e.target.value)} className="h-8" disabled={!puedeEditarMet} />
              </div>
            </div>
          </div>

          {/* Escala Medio de pago (Fase 2) — 5 selectores */}
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="ebr-escala-medio-pago">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Escala de valoración — Medio de pago preferido</p>
            <p className="mb-3 text-xs text-slate-500">Nivel base por medio de pago capturado en el expediente (<b>perfil_medio_pago</b>).</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {MP_ORDER.map(({ key, label }) => (
                <div key={key} className="rounded-md border border-slate-200 bg-white px-2.5 py-2" data-testid={`ebr-escala-mp-${key}`}>
                  <p className="text-xs text-slate-500">{label}</p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <select data-testid={`ebr-escala-mp-${key}-select`} className="h-8 flex-1 rounded border border-slate-300 px-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                      value={escalasMp[key] || "bajo"} disabled={!puedeEditarMet}
                      onChange={(e) => setEscalaMp(key, e.target.value)}>
                      <option value="bajo">Bajo</option>
                      <option value="medio">Medio</option>
                      <option value="alto">Alto</option>
                    </select>
                    <Badge className={NIVEL_COLOR[escalasMp[key] || "bajo"]}>{(escalasMp[key] || "bajo").toUpperCase()}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Escala Canal (Fase 2) — 5 selectores */}
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="ebr-escala-canal">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Escala de valoración — Canal de contratación</p>
            <p className="mb-3 text-xs text-slate-500">Nivel base por canal de contratación capturado en el expediente (<b>canal_contratacion</b>).</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {CANAL_ORDER.map(({ key, label }) => (
                <div key={key} className="rounded-md border border-slate-200 bg-white px-2.5 py-2" data-testid={`ebr-escala-canal-${key}`}>
                  <p className="text-xs text-slate-500">{label}</p>
                  <div className="mt-1 flex items-center gap-1.5">
                    <select data-testid={`ebr-escala-canal-${key}-select`} className="h-8 flex-1 rounded border border-slate-300 px-2 text-sm disabled:bg-slate-100 disabled:text-slate-500"
                      value={escalasCanal[key] || "bajo"} disabled={!puedeEditarMet}
                      onChange={(e) => setEscalaCanal(key, e.target.value)}>
                      <option value="bajo">Bajo</option>
                      <option value="medio">Medio</option>
                      <option value="alto">Alto</option>
                    </select>
                    <Badge className={NIVEL_COLOR[escalasCanal[key] || "bajo"]}>{(escalasCanal[key] || "bajo").toUpperCase()}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Catálogos de alto riesgo (auto-derivan el score por cliente) */}
      {puedeCatalogos && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="ebr-catalogos">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-900"><Tag className="h-4 w-4 text-primary" /> Catálogos de alto riesgo de la entidad</h3>
            <Button data-testid="cfg-guardar-btn" size="sm" disabled={savingCat} onClick={guardarCatalogos} className="bg-accent hover:bg-forest-700">
              {savingCat ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Database className="mr-1.5 h-4 w-4" />} Guardar catálogos
            </Button>
          </div>
          {/* Evidencia de último guardado (CNBV). NUNCA usa la palabra "sellado" ni "versión":
              eso es de la metodología. Los catálogos son operativos y se aplican en vivo. */}
          <p className="mb-2 text-[11px] text-slate-500" data-testid="ebr-catalogos-evidencia">
            {cfgMeta.fecha
              ? <>Catálogos guardados el <b>{new Date(cfgMeta.fecha).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</b> por <b>{cfgMeta.por}</b>{cfgMeta.rol ? ` (${cfgMeta.rol})` : ""}.</>
              : <>Catálogos sin configurar.</>}
          </p>
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="ebr-catalogos-aviso">
            Estos catálogos aplican de inmediato al cálculo de riesgo de nuevos expedientes.
            No requieren nueva firma de la metodología. La próxima versión que selle el Oficial
            de Cumplimiento absorberá los catálogos vigentes.
          </div>
          <p className="mb-3 text-xs text-slate-500">El cálculo automático clasifica una subvariable en <b>Alto</b> cuando el dato del expediente del cliente coincide con estos catálogos. Los países GAFI se gestionan en el módulo de Listas/Sanciones (PLD). Los catálogos usan las mismas listas cerradas que el alta de cliente, para cruce exacto.</p>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            <CatalogoSelectChips label="Zonas / plazas de alto riesgo" testid="zona"
              items={cfg.zonas_alto_riesgo}
              options={ESTADOS_MX.map((e) => ({ value: e, label: e }))}
              placeholder="Seleccionar estado…"
              onAdd={(v) => addCat("zonas_alto_riesgo", v)} onRemove={(v) => rmCat("zonas_alto_riesgo", v)} />
            <CatalogoSelectChips label="Productos / destinos de alto riesgo" testid="prod"
              items={cfg.productos_alto_riesgo}
              options={(productosEntidad || []).map((p) => ({ value: p.nombre, label: p.nombre }))}
              placeholder={productosEntidad.length === 0 ? "Configura productos primero" : "Seleccionar producto…"}
              onAdd={(v) => addCat("productos_alto_riesgo", v)} onRemove={(v) => rmCat("productos_alto_riesgo", v)} />
            <CatalogoSelectChips label="Actividades económicas de alto riesgo" testid="act"
              items={cfg.actividades_alto_riesgo}
              options={ACTIVIDADES_ECONOMICAS.map((a) => ({ value: a.value, label: `${a.value}${a.av ? " [AV]" : ""}` }))}
              placeholder="Seleccionar actividad…"
              itemLabel={(z) => `${z}${ACTIVIDADES_VULNERABLES.has(z) ? " [AV]" : ""}`}
              onAdd={(v) => addCat("actividades_alto_riesgo", v)} onRemove={(v) => rmCat("actividades_alto_riesgo", v)} />
          </div>
          <p className="mt-2 text-xs text-slate-400">Las actividades marcadas <b>[AV]</b> ya elevan automáticamente la subvariable de actividad del cliente por Art. 17 LFPIORPI (sin necesidad de agregarlas aquí). Este catálogo es para giros adicionales que tu SOFOM considere de alto riesgo.</p>
        </div>
      )}

      {/* Historial de versiones de la metodología (gobernanza CNBV) */}
      {puedeEditar && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="ebr-historial">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-900"><History className="h-4 w-4 text-primary" /> Historial de versiones de la metodología</h3>
            <span className="text-xs text-slate-400">Revisión cada 12 meses · conservación 5 años · sello de quién aprobó cada versión</span>
          </div>
          <DataTable testid="table-ebr-historial" rows={historial}
            columns={[
              { key: "version", label: "Versión", render: (r) => (
                <span className="flex items-center gap-1.5">v{r.version}{r.vigente && <Badge className="bg-emerald-100 text-emerald-700">Vigente</Badge>}</span>
              ) },
              { key: "fecha", label: "Fecha", kind: "date" },
              { key: "aprobado_por", label: "Aprobada por", render: (r) => (
                <span>{r.aprobado_por || "—"}{r.aprobado_rol ? <span className="text-xs text-slate-400"> ({r.aprobado_rol})</span> : null}</span>
              ) },
              { key: "cambios", label: "Cambios", render: (r) => (
                <span className="text-xs text-slate-600">{(r.cambios || []).length ? (r.cambios || []).join(" · ") : "—"}</span>
              ) },
            ]}
            actions={(row) => (
              <Button data-testid={`ebr-evidencia-${row.version}`} size="sm" variant="ghost" onClick={() => descargarEvidencia(row)}>
                <FileDown className="mr-1.5 h-4 w-4" /> PDF
              </Button>
            )}
            emptyLabel="Sin versiones registradas" />
        </div>
      )}

      {/* Bandeja */}
      {bandeja && (
        <div className="space-y-4">
          <div>
            <p className="mb-1.5 flex items-center gap-2 text-sm font-medium text-red-700"><AlertTriangle className="h-4 w-4" /> Rojos pendientes de autorización del OC</p>
            <DataTable testid="table-ebr-pendientes" rows={bandeja.pendientes}
              columns={[
                { key: "nombre", label: "Cliente" }, { key: "rfc", label: "RFC" },
                { key: "puntaje_riesgo", label: "Puntaje" },
                { key: "riesgo_motivo", label: "Motivo", render: (r) => MOTIVO_LABEL[r.riesgo_motivo] || r.riesgo_motivo },
              ]} emptyLabel="Sin clientes pendientes de autorización" />
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <p className="mb-1.5 flex items-center gap-2 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Operación autorizada (override con carta)</p>
              <DataTable testid="table-ebr-autorizados" rows={bandeja.autorizados}
                columns={[{ key: "nombre", label: "Cliente" }, { key: "override_por", label: "Autorizó" }, { key: "override_fecha", label: "Fecha", kind: "date" }]}
                emptyLabel="Sin autorizaciones" />
            </div>
            <div>
              <p className="mb-1.5 flex items-center gap-2 text-sm font-medium text-red-700"><ShieldX className="h-4 w-4" /> Bloqueados por LPB (sin override posible)</p>
              <DataTable testid="table-ebr-bloqueados" rows={bandeja.bloqueados}
                columns={[{ key: "nombre", label: "Cliente" }, { key: "rfc", label: "RFC" }]}
                emptyLabel="Sin clientes bloqueados" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
